import { nanoid } from '@blocksuite/store';
import { SELECT_OPTION_COLOR_BY_NAME } from '@streamlain/database-identity';

import type { GanttSingleView } from '../../gantt-view-manager.js';
import type { DetectedColumns } from './column-detection-service.js';

type GanttSetupRole =
  | 'start'
  | 'end'
  | 'progress'
  | 'status'
  | 'assignee'
  | 'dependencies';
export interface GanttSetupPlan {
  readonly datesMode: 'auto' | 'existing';
  readonly singleDateRole: 'start' | 'end';
  readonly columns: Readonly<Record<GanttSetupRole, string | undefined>>;
  readonly createNames: Readonly<Record<GanttSetupRole, string | null>>;
  readonly dependencies: boolean;
}

const DEFAULT_STATUS_OPTIONS: ReadonlyArray<readonly [string, string]> = [
  ['Todo', SELECT_OPTION_COLOR_BY_NAME.Orange],
  ['In Progress', SELECT_OPTION_COLOR_BY_NAME.Blue],
  ['Blocked', SELECT_OPTION_COLOR_BY_NAME.Magenta],
  ['Done', SELECT_OPTION_COLOR_BY_NAME.Green],
];

/** Applies the pure wizard plan as one canonical schema publication and undo step. */
export function applyGanttSetup(
  view: GanttSingleView,
  plan: GanttSetupPlan,
  detected: DetectedColumns
): void {
  if (view.readonly$.value) return;
  view.manager.dataSource.atomicChange(() =>
    new GanttSetupApplication(view, plan).apply(detected)
  );
}
class GanttSetupApplication {
  constructor(
    private readonly view: GanttSingleView,
    private readonly plan: GanttSetupPlan
  ) {}
  apply(detected: DetectedColumns): void {
    this.applyDates(detected);
    this.applyProgress(detected);
    this.applyStatus(detected);
    this.applyAssignee(detected);
    this.applyDependencies(detected);
  }

  private applyDates(detected: DetectedColumns): void {
    if (this.plan.datesMode === 'auto') {
      const startId = this.view.propertyAdd('end', {
        type: 'date',
        name: 'Start',
      });
      const endId = this.view.propertyAdd('end', {
        type: 'date',
        name: 'End',
      });
      if (startId) this.view.setStartDateCol(startId);
      if (endId) this.view.setEndDateCol(endId);
      return;
    }

    const count = detected.date.length;
    if (count === 1) {
      const existing = detected.date[0];
      if (!existing) return;
      const missingName =
        this.plan.singleDateRole === 'start' ? 'End' : 'Start';
      const missingId = this.view.propertyAdd('end', {
        type: 'date',
        name: missingName,
      });
      if (this.plan.singleDateRole === 'start') {
        this.view.setStartDateCol(existing.id);
        if (missingId) this.view.setEndDateCol(missingId);
      } else {
        this.view.setEndDateCol(existing.id);
        if (missingId) this.view.setStartDateCol(missingId);
      }
      return;
    }

    const startId =
      this.resolvePickerId(
        this.plan.columns.start,
        this.plan.createNames.start,
        'date',
        'Start'
      ) ?? this.view.propertyAdd('end', { type: 'date', name: 'Start' });
    const endId =
      this.resolvePickerId(
        this.plan.columns.end,
        this.plan.createNames.end,
        'date',
        'End'
      ) ?? this.view.propertyAdd('end', { type: 'date', name: 'End' });
    this.view.setStartDateCol(startId);
    this.view.setEndDateCol(endId);
  }

  /**
   * Resolves a picker's final property id for Apply. When the user
   * typed a name into the inline "create new" input (`creating`
   * is non-null), a new column is added with that name (falling back
   * to the default when the field was left blank). Otherwise the
   * existing `bound` wins.
   */
  private resolvePickerId(
    bound: string | undefined,
    creating: string | null,
    type: string,
    defaultName: string,
    afterCreate?: (id: string) => void
  ): string | undefined {
    if (creating !== null) {
      const name = creating.trim() || defaultName;
      const id = this.view.propertyAdd('end', { type, name });
      if (id && afterCreate) afterCreate(id);
      return id ?? undefined;
    }
    return bound;
  }

  private applyProgress(detected: DetectedColumns): void {
    if (this.plan.createNames.progress !== null) {
      this.view.setProgressCol(
        this.resolvePickerId(
          this.plan.columns.progress,
          this.plan.createNames.progress,
          'progress',
          'Progress'
        )
      );
      return;
    }
    if (detected.progress.length > 0 && this.plan.columns.progress) {
      this.view.setProgressCol(this.plan.columns.progress);
      return;
    }
    const newId = this.view.propertyAdd('end', {
      type: 'progress',
      name: 'Progress',
    });
    this.view.setProgressCol(newId);
  }

  private applyStatus(detected: DetectedColumns): void {
    if (this.plan.createNames.status !== null) {
      const id = this.resolvePickerId(
        this.plan.columns.status,
        this.plan.createNames.status,
        'select',
        'Status',
        created => this.seedStatusOptions(created)
      );
      this.view.setStatusCol(id);
      return;
    }
    if (detected.status.length > 0 && this.plan.columns.status) {
      this.view.setStatusCol(this.plan.columns.status);
      return;
    }
    const newId = this.view.propertyAdd('end', {
      type: 'select',
      name: 'Status',
    });
    if (!newId) {
      this.view.setStatusCol(undefined);
      return;
    }
    this.seedStatusOptions(newId);
    this.view.setStatusCol(newId);
  }

  private applyAssignee(detected: DetectedColumns): void {
    if (this.plan.createNames.assignee !== null) {
      this.view.setAssigneeCol(
        this.resolvePickerId(
          this.plan.columns.assignee,
          this.plan.createNames.assignee,
          'member',
          'Assignee'
        )
      );
      return;
    }
    if (detected.assignee.length > 0 && this.plan.columns.assignee) {
      this.view.setAssigneeCol(this.plan.columns.assignee);
      return;
    }
    const newId = this.view.propertyAdd('end', {
      type: 'member',
      name: 'Assignee',
    });
    this.view.setAssigneeCol(newId);
  }

  /**
   * Seed a freshly created Status column with Todo / In Progress /
   * Blocked / Done chips. Goes through the `cell.property.dataUpdate`
   * helper — the same mutation path the multi-tag popover uses when the
   * user creates a tag by hand — so the select cell's `options`
   * signal fires and the table view re-renders with the chips. The
   * previous `dataSource.propertyDataSet` write bypassed that signal
   * and left the table view painting empty chips for seeded rows.
   */
  private seedStatusOptions(propertyId: string): void {
    const property = this.view.propertyGetOrCreate(propertyId);
    if (!property) return;
    const options = DEFAULT_STATUS_OPTIONS.map(([value, color]) => ({
      id: nanoid(),
      value,
      color,
    }));
    property.dataUpdate(data => ({
      ...(data as Record<string, unknown>),
      options,
    }));
  }

  private applyDependencies(detected: DetectedColumns): void {
    if (!this.plan.dependencies) {
      this.view.setDependenciesCol(undefined);
      return;
    }
    if (
      this.plan.columns.dependencies &&
      this.plan.createNames.dependencies === null
    ) {
      this.view.setDependenciesCol(this.plan.columns.dependencies);
      return;
    }
    if (!detected.selfBlockId) {
      this.view.setDependenciesCol(undefined);
      return;
    }
    const name = this.plan.createNames.dependencies?.trim() || 'Dependencies';
    const forwardId = this.view.propertyAdd('end', {
      type: 'relation',
      name,
    });
    if (!forwardId) {
      this.view.setDependenciesCol(undefined);
      return;
    }
    const backlinkName =
      name === 'Dependencies' ? 'Dependents' : `${name} Backlink`;
    const backlinkId = this.view.propertyAdd('end', {
      type: 'relation',
      name: backlinkName,
    });
    const titleCol = this.view.mainProperties$.value.titleColumn ?? '';
    const dataSource = this.view.manager.dataSource;
    dataSource.propertyDataSet(forwardId, {
      targetDatabaseId: detected.selfBlockId,
      targetFieldId: titleCol,
      cardinality: 'many',
      inverseFieldId: backlinkId,
      inverseCardinality: 'many',
    });
    if (backlinkId) {
      dataSource.propertyDataSet(backlinkId, {
        targetDatabaseId: detected.selfBlockId,
        targetFieldId: titleCol,
        cardinality: 'many',
        inverseFieldId: forwardId,
        inverseCardinality: 'many',
      });
    }
    this.view.setDependenciesCol(forwardId);
  }
}
